import { FC, useState } from "react"
import { ThumbsDown, ThumbsUp } from "lucide-react"
import {
  deleteStaffFeedback,
  StaffFeedbackReason,
  submitStaffFeedback
} from "../../../api/staff-agent"

const REASONS: { value: StaffFeedbackReason; label: string }[] = [
  { value: "incorrect", label: "Incorrect" },
  { value: "incomplete", label: "Incomplete" },
  { value: "confusing", label: "Confusing" },
  { value: "wrong_data_or_scope", label: "Wrong data or scope" },
  { value: "too_slow", label: "Too slow" },
  { value: "missing_capability", label: "Couldn't do what I needed" },
  { value: "unexpected_action", label: "Unexpected action" },
  { value: "other", label: "Other" }
]

interface Props {
  targetId: string
  conversationId?: string
  initial?: {
    sentiment: "helpful" | "not_helpful"
    reasons: StaffFeedbackReason[]
  }
}

const FeedbackControls: FC<Props> = ({ targetId, conversationId, initial }) => {
  const [sentiment, setSentiment] = useState(initial?.sentiment)
  const [reasons, setReasons] = useState<StaffFeedbackReason[]>(
    initial?.reasons ?? []
  )
  const [comment, setComment] = useState("")
  const [showReasons, setShowReasons] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const chooseHelpful = async () => {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      if (sentiment === "helpful") {
        await deleteStaffFeedback(targetId)
        setSentiment(undefined)
      } else {
        await submitStaffFeedback({
          targetId,
          conversationId,
          sentiment: "helpful"
        })
        setSentiment("helpful")
        setReasons([])
        setShowReasons(false)
      }
    } catch (requestError: any) {
      setError(requestError?.message || "Could not save feedback")
    } finally {
      setBusy(false)
    }
  }

  const submitNegative = async () => {
    if (busy || reasons.length === 0) return
    setBusy(true)
    setError(null)
    try {
      await submitStaffFeedback({
        targetId,
        conversationId,
        sentiment: "not_helpful",
        reasons,
        comment: comment.trim() || undefined
      })
      setSentiment("not_helpful")
      setShowReasons(false)
    } catch (requestError: any) {
      setError(requestError?.message || "Could not save feedback")
    } finally {
      setBusy(false)
    }
  }

  const chooseNotHelpful = async () => {
    if (sentiment === "not_helpful" && !showReasons) {
      setBusy(true)
      setError(null)
      try {
        await deleteStaffFeedback(targetId)
        setSentiment(undefined)
        setReasons([])
      } catch (requestError: any) {
        setError(requestError?.message || "Could not remove feedback")
      } finally {
        setBusy(false)
      }
      return
    }
    setShowReasons(true)
  }

  const toggleReason = (reason: StaffFeedbackReason) => {
    setReasons(current =>
      current.includes(reason)
        ? current.filter(item => item !== reason)
        : current.length < 4
        ? [...current, reason]
        : current
    )
  }

  return (
    <div className="mt-2 border-t border-gray-100 pt-2">
      <div className="flex items-center gap-1">
        <span className="mr-1 text-[11px] text-neutral-400">Helpful?</span>
        <button
          type="button"
          disabled={busy}
          onClick={chooseHelpful}
          aria-label="Mark response helpful"
          aria-pressed={sentiment === "helpful"}
          className={`rounded p-1 transition-colors ${
            sentiment === "helpful"
              ? "bg-emerald-50 text-emerald-700"
              : "text-neutral-400 hover:bg-gray-100 hover:text-neutral-600"
          }`}
        >
          <ThumbsUp size={14} />
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={chooseNotHelpful}
          aria-label="Mark response not helpful"
          aria-pressed={sentiment === "not_helpful"}
          className={`rounded p-1 transition-colors ${
            sentiment === "not_helpful"
              ? "bg-red-50 text-red-700"
              : "text-neutral-400 hover:bg-gray-100 hover:text-neutral-600"
          }`}
        >
          <ThumbsDown size={14} />
        </button>
      </div>

      {showReasons && (
        <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-2.5">
          <p className="text-xs font-medium text-neutral-700">
            What could be improved?
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {REASONS.map(reason => (
              <button
                key={reason.value}
                type="button"
                onClick={() => toggleReason(reason.value)}
                className={`rounded-full border px-2 py-1 text-xs transition-colors ${
                  reasons.includes(reason.value)
                    ? "border-violet-300 bg-violet-50 text-violet-700"
                    : "border-gray-200 bg-white text-neutral-600 hover:border-gray-300"
                }`}
              >
                {reason.label}
              </button>
            ))}
          </div>
          <textarea
            value={comment}
            maxLength={1000}
            rows={2}
            onChange={event => setComment(event.target.value)}
            placeholder="Optional details—please don't include borrower personal information"
            className="mt-2 w-full resize-none rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
          />
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              disabled={busy || reasons.length === 0}
              onClick={submitNegative}
              className="rounded-lg bg-violet-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-violet-700 disabled:opacity-40"
            >
              Send feedback
            </button>
            <button
              type="button"
              onClick={() => setShowReasons(false)}
              className="px-2 py-1.5 text-xs text-neutral-500 hover:text-neutral-700"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}

export default FeedbackControls
